import React from 'react';

interface TipCardProps {
    tip: {
        id: string;
        title: string;
        content: string;
        category: string;
    };
}

export default function TipCard({ tip }: TipCardProps) {
    const [isExpanded, setIsExpanded] = React.useState(true);

    const categoryColors: Record<string, string> = {
        'Best Practices': 'bg-blue-100 text-blue-800',
        'Productivity': 'bg-green-100 text-green-800',
        'Code Quality': 'bg-purple-100 text-purple-800',
        'Testing': 'bg-yellow-100 text-yellow-800',
        'Collaboration': 'bg-pink-100 text-pink-800',
        'Tools': 'bg-indigo-100 text-indigo-800',
        'Security': 'bg-red-100 text-red-800',
        'Performance': 'bg-orange-100 text-orange-800',
    };

    const categoryColor = categoryColors[tip.category] || 'bg-gray-100 text-gray-800';

    if (!isExpanded) {
        return (
            <button
                onClick={() => setIsExpanded(true)}
                className="w-full text-left bg-surface border border-primary/30 rounded-lg p-3 hover:shadow-md transition flex items-center justify-between"
            >
                <div className="flex items-center space-x-2">
                    <span className="text-xl">💡</span>
                    <span className="text-sm font-medium text-text-primary">Show today's tip</span>
                </div>
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
        );
    }

    return (
        <div className="bg-surface border border-primary/30 rounded-lg p-6 shadow-sm">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <span className="text-2xl">💡</span>
                    <h3 className="text-lg font-bold text-text-primary">{tip.title}</h3>
                </div>
                <button
                    onClick={() => setIsExpanded(false)}
                    className="text-text-secondary hover:text-text-primary transition"
                    aria-label="Collapse tip"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                </button>
            </div>

            <p className="text-text-secondary mb-3 leading-relaxed">{tip.content}</p>

            <div className="flex items-center justify-between">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${categoryColor}`}>
                    {tip.category}
                </span>
            </div>
        </div>
    );
}

export function TipCardSkeleton() {
    return (
        <div className="bg-surface border border-primary/30 rounded-lg p-6 shadow-sm animate-pulse">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2 w-full">
                    <div className="w-8 h-8 bg-gray-200 rounded-full dark:bg-gray-700"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/3 dark:bg-gray-700"></div>
                </div>
                <div className="w-5 h-5 bg-gray-200 rounded dark:bg-gray-700"></div>
            </div>

            <div className="space-y-2 mb-3">
                <div className="h-4 bg-gray-200 rounded w-full dark:bg-gray-700"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 dark:bg-gray-700"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6 dark:bg-gray-700"></div>
            </div>

            <div className="flex items-center justify-between">
                <div className="h-6 w-24 bg-gray-200 rounded-full dark:bg-gray-700"></div>
            </div>
        </div>
    );
}
